import React, { forwardRef, useState, useEffect, useCallback, useRef } from 'react';

import { store } from '../../app/store';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import CallRecord from '../../models/CallRecord';
import CallRecordService from '../../Services/Call/CallRecord';
import SaleforceService  from '../../Services/SaleforceService';
import TwilioVoiceService from '../../Services/TwilioVoiceService';
import CallCentreService from '../../Services/CallCentre/CallCentre';
import CallNotificationService from '../../Services/Call/CallNotification';
import TenantService from '../../Services/Tenant/TenantService';
import { CallBarState } from '../../constants/CallBarState';
import { SnackbarType } from '../../constants/SnackbarType';
import { FinalizeEnum } from '../../constants/SkipWrapupEnum';
import { DefaultUserStatuses } from '../../constants/UserStatusesEnum';
import { showSnackbar } from '../../slices/snackbar';
import CallConstants from '../../constants/Call';
import { getUserWrapupStatus, addLastCallRecord, setUserOutboundCallNumberState } from '../../slices/auth';
import { updateConnectionStatus } from '../../slices/networkConnection';
import { updateCallBarState, setIncomingCallIdle, setCallTimer, updateCurrentActiveCallRecordId, setIsCurrentCallInHold } from '../../slices/callCentre';
import HomeComponent from '../Home';
import TopNavBar from '../TopNavBar';
import CallOutcome from '../CallOutcome';
import IncomingCallPanel from '../IncomingCallPanel';
import NavigationDiallerContainer from '../NavigationDiallerContainer';
import { logger } from '../../Util/Logger/ClientLogger';
import { NetworkAvailability } from '../../Util/NetworkAvailability';
import { appDebugLog } from '../../Util/appDebugLog';
import { requestRemoteAnswer, requestRemoteDecline } from '../../Util/incomingUiRelay';

import { MainProps } from './Main.types';

const LogTitle = 'MainComponent';

/** Defer clearing incoming-call Redux so a large unmount/render does not run before Twilio finishes pending→open. */
const INCOMING_CALL_IDLE_DEFER_MS = 1000;

/** After reject/disconnect, yield one frame so the SDK processes before Redux swaps the tree. */
const INCOMING_DECLINE_UI_DEFER_MS = 50;

const resolveCallerIdType = (option: any) => {
  if (option?.usedByType === 'Org') return CallConstants.OutgoingCallIdEnums.Organisation;
  if (option?.usedByType === 'Team') return CallConstants.OutgoingCallIdEnums.Team;
  return CallConstants.OutgoingCallIdEnums.Personal;
};

const MainComponent = forwardRef<HTMLDivElement, MainProps>((props, ref) => {
  const [dialpadOpen, setDialpadOpen] = useState(false);
  const [showCallOutcome, setShowCallOutcome] = useState(false);
  const [selectedSfContact, setSelectedSfContact] = useState<null | any>(null);
  const [dialOutOptions, setDialOutOptions] = useState<any[]>([]);
  const [dialOutOptionsFetched, setDialOutOptionsFetched] = useState(false);
  const outboundNumberSyncedRef = useRef(false);
  
  const authData = useAppSelector((state: any) => state.auth);
  const settingsData = useAppSelector((state: any) => state.settings);
  const callBarStateData = useAppSelector((state: any) => state.callCentre.callBarState);
  const callTimer = useAppSelector((state: any) => state.callCentre.callTimer);
  const isCurrentCallInHold = useAppSelector((state: any) => state.callCentre.isCurrentCallInHold);
  const callIncoming = useAppSelector((state: any) => state.callCentre.incomingCallInfo?.callIncoming)
  const isCallDispositionNotMandatory = useAppSelector((state: any) => state.settings?.tenantSettings?.tenantCallSettings?.isCallDispositionNotMandatory);
  const getHelpAvailable = useAppSelector((state: any) => state.settings?.tenantSettings?.tenantCallSettings?.getHelpAvailable);
  const currentActiveCallRecordId = useAppSelector((state: any) => state.callCentre?.currentActiveCallRecordId);
  const dispatch = useAppDispatch();

  const incomingIdleTimerRef = useRef<number | null>(null);
  const declineUiTimerRef = useRef<number | null>(null);

  const clearPendingIncomingTimers = useCallback(() => {
    if (incomingIdleTimerRef.current != null) {
      window.clearTimeout(incomingIdleTimerRef.current);
      incomingIdleTimerRef.current = null;
    }
    if (declineUiTimerRef.current != null) {
      window.clearTimeout(declineUiTimerRef.current);
      declineUiTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearPendingIncomingTimers();
    };
  }, [clearPendingIncomingTimers]);

  const showNavigationDialler = !showCallOutcome &&
    callBarStateData !== CallBarState.IDLE &&
    callBarStateData !== CallBarState.WRAP_UP &&
    callBarStateData !== CallBarState.POST_CALL &&
    !!callBarStateData;

  function onPublishMessage(message: any) {
    SaleforceService.processLWCMessage(message);
  }

  const setupSForce = useCallback(() => {
     // @ts-ignore
     sforce?.opencti?.enableClickToDial({
      callback: (response: any) => {
        if (response.success) {
          appDebugLog("API method call executed successfully! returnValue:", response.returnValue);
        } else {
           console.error('Something went wrong on enabling click to dial!', response.errors);
           logger.error('Something went wrong on enabling click to dial!', LogTitle, { error: response.errors });
        }
      },
     });

    // @ts-ignore
    sforce?.opencti?.onClickToDial({
      listener: (payload: any) => {
        if (store.getState()?.callCentre?.callBarState === CallBarState.IDLE) {
          SaleforceService.setSoftPhoneVisibleIfNot();
          
          SaleforceService.getObjectDetails(payload.objectType, payload.recordId, payload.number)
            .then((result) => {
              result = {...result, dialNumber: payload.number};
              setSelectedSfContact(result);
            })
            .catch((error) => {
              const contact = { firstName: payload.recordName, mobilePhone: payload.number, remoteId: payload.recordId, contactType: payload?.objectType}
              setSelectedSfContact(contact);
            });

          setDialpadOpen(true);
          setShowCallOutcome(false);
          /*
           {
            "url":
            "https://MyDomainNamemy.salesforce.com/lightning/r/Account/001x0000003DGQR/view",
            }
            "recordId": "001x0000003DGQR",
            "recordName": "Acme Person Account",
            "objectType": "Account",
            "contactId": "003D000000QOMqg",
            "personAccount": true
          }
           */
        } else {
          SaleforceService.setSoftPhoneVisibleIfNot();
          const snackbarData = { message: 'It seems you were in a call that needs to be closed before you can make any further calls.'};
          store.dispatch(showSnackbar({ snackbarType: SnackbarType.Error, snackbarData }));
        }
      }
    });

    // Subscribe For LWC event
    // @ts-ignore
    sforce.opencti.subscribe({
      channelName: "rocketphone__LWCTORPIS__c",
      listener: onPublishMessage,
      callback: (result: any) => {
        if (result.success) {
          appDebugLog(result.subscription);
        } else {
          appDebugLog(result.errors);
        }
      }
    });
  }, [callBarStateData, setDialpadOpen, setSelectedSfContact]);

  // End SForce work

  useEffect(() => {
    setupSForce();
  }, []);

  // Dial-out list is tenant-scoped; do not re-fetch on every AccessToken rotation (token refresh).
  useEffect(() => {
    const fetchDialOutOptions = async () => {
      const { tenantCode, AccessToken } = store.getState().auth?.authUser ?? {};
      if (tenantCode && AccessToken) {
        try {
          const response = await TenantService.getDialOutOptions(tenantCode, AccessToken);
          setDialOutOptions(response?.data?.data?.phoneNumbers || []);
          setDialOutOptionsFetched(true);
        } catch (error) {
          logger.warn('Failed to fetch dial out options', LogTitle, { error });
          setDialOutOptions([]);
        }
      }
    };

    fetchDialOutOptions();
  }, [authData?.authUser?.tenantCode]);

  // Runs once, after both the user profile (getUser) and the dial-out options have loaded.
  useEffect(() => {
    if (outboundNumberSyncedRef.current || !dialOutOptionsFetched || !authData?.user?.id) return;

    outboundNumberSyncedRef.current = true;

    const selectableOptions = dialOutOptions.filter((option: any) => option?.phoneNumber && option?.allocationType !== 'RC');
    if (!selectableOptions.length) return;

    const savedPhoneNumberId = authData.user.preferences?.outgoingCallIdPreference?.phoneNumberId;
    const selectedOption = selectableOptions.find((option: any) => option?.id === savedPhoneNumberId) || selectableOptions[0];

    dispatch(setUserOutboundCallNumberState({
      phoneNumberId: selectedOption.id,
      callerIdType: resolveCallerIdType(selectedOption),
    }));
  }, [dialOutOptionsFetched, dialOutOptions, authData?.user?.id, authData?.user?.preferences, dispatch]);

  useEffect(() => {
    NetworkAvailability.init();
    return NetworkAvailability.subscribe((online) => {
      dispatch(updateConnectionStatus({ connectionAvailable: online }));
    });
  }, [dispatch]);

  useEffect(() => {
    // we have to show call outcome page if the call was hung up from customer side
    if (callBarStateData === CallBarState.WRAP_UP || callBarStateData === CallBarState.POST_CALL) {
      setShowCallOutcome(true);
      setDialpadOpen(false);
    } else if (callBarStateData === CallBarState.IDLE) {
      setDialpadOpen(false);
    }
  }, [callBarStateData]);

  const handleDialPadClick = () => {
    setShowCallOutcome(false);
    setDialpadOpen(true);
  };

  const handleHomeClick = () => {
    setDialpadOpen(false);
    dispatch(updateCallBarState(CallBarState.IDLE))
  };

  const handleCallHangupClicked = () => {
    if (isCallDispositionNotMandatory) {
      dispatch(updateCallBarState(CallBarState.IDLE));
      setDialpadOpen(false);

      if (!currentActiveCallRecordId) {
        TwilioVoiceService.updateUserAvailableStatus(null, false);
      }
    } else {
      dispatch(updateCallBarState(CallBarState.WRAP_UP))
      setShowCallOutcome(true);
    }
    
    setDialpadOpen(false);

    // dispatch(getUserWrapupStatus(authData.authUser?.userId));
   
    if (isCurrentCallInHold) {
      CallCentreService.hangupCall(currentActiveCallRecordId, authData?.authUser?.tenantCode, authData?.authUser?.AccessToken);

      dispatch(updateCurrentActiveCallRecordId(null));
      dispatch(setIsCurrentCallInHold(false));
      TwilioVoiceService.updateUserAvailableStatus(null, false);
    }
  };

  const handleNavigationDiallerUnmount = () => {
    setSelectedSfContact(null);
  };

  const finalizeCallRecord = async (callOutcome: string, callRecord: any, callDuration: any) => {
    const tenantUserStatusesState = settingsData?.tenantSettings?.userStatuses;
    const onCallStatus = tenantUserStatusesState?.filter((item: any) => item?.name === DefaultUserStatuses.OnCall)?.[0];
    const userOnCall = authData.user?.userStatus?.id === onCallStatus?.id;
    const finalizeRequest = new CallRecord.FinalizeRequest(callRecord?.callRecordId, FinalizeEnum.completed, callOutcome, callDuration);
    let result = true;

    try {
      await CallRecordService.finalizeCallRecord(authData?.authUser?.tenantCode, authData?.authUser?.AccessToken, finalizeRequest);
      dispatch(getUserWrapupStatus(authData.authUser?.userId));
      dispatch(addLastCallRecord(null));
      setShowCallOutcome(false);
      setDialpadOpen(false);
      
      if (userOnCall) {
        TwilioVoiceService.updateUserAvailableStatus(null, false);
      }
      dispatch(setCallTimer({ callDuration: 0 }));
    } catch (error) {
      result = false;
    }

    return result;
  }

  const handleCalloutcomeSubmitClick = (callOutcome: string) => {
    const callRecord = authData?.lastCallRecord ? authData?.lastCallRecord : {callRecordId: authData?.userWrapUpStatus?.callRecordID};
    dispatch(updateCallBarState(CallBarState.IDLE))

    finalizeCallRecord(callOutcome, callRecord, callTimer);
  };

  const handleCallWrapup = () => {
    setShowCallOutcome(true);
    setDialpadOpen(false);
  };
  
  const handleCallAnswer = useCallback(() => {
    clearPendingIncomingTimers();

    const connection = TwilioVoiceService.callConnection;
    const incomingInfo = store.getState()?.callCentre?.incomingCallInfo;
    if (!connection) {
      if (incomingInfo?.callIncoming) {
        requestRemoteAnswer();
        CallNotificationService.hideIncomingCallNotification(incomingInfo?.callDetails?.callRecordId);
        dispatch(setIncomingCallIdle());
        return;
      }
      dispatch(updateCallBarState(CallBarState.IDLE));
      dispatch(setIncomingCallIdle());
      return;
    }

    // Accept first: Twilio expects a fast answer; Redux + unmounting the incoming panel first can race timeouts.
    // @ts-ignore Twilio Voice Call.status() — only accept once while still pending.
    const status = typeof connection.status === 'function' ? connection.status() : '';
    if (status === 'pending' || status === 'ringing') {
      // @ts-ignore
      connection.accept();
    }

    dispatch(updateCallBarState(CallBarState.IN_CALL));
    setShowCallOutcome(false);

    const callDetails =
      TwilioVoiceService.getCallDetailsFromConnection(connection);
    CallNotificationService.hideIncomingCallNotification(
      callDetails?.callRecordId
    );

    incomingIdleTimerRef.current = window.setTimeout(() => {
      incomingIdleTimerRef.current = null;
      dispatch(setIncomingCallIdle());
    }, INCOMING_CALL_IDLE_DEFER_MS);
  }, [dispatch, clearPendingIncomingTimers]);

  const handleCallHangup = useCallback(() => {
    clearPendingIncomingTimers();

    const connection = TwilioVoiceService.callConnection;
    const incomingInfo = store.getState()?.callCentre?.incomingCallInfo;
    if (!connection && incomingInfo?.callIncoming) {
      requestRemoteDecline();
      CallNotificationService.hideIncomingCallNotification(incomingInfo?.callDetails?.callRecordId);
      declineUiTimerRef.current = window.setTimeout(() => {
        declineUiTimerRef.current = null;
        dispatch(updateCallBarState(CallBarState.IDLE));
        dispatch(setIncomingCallIdle());
      }, INCOMING_DECLINE_UI_DEFER_MS);
      return;
    }
    if (connection) {
      const callDetails =
        TwilioVoiceService.getCallDetailsFromConnection(connection);
      // @ts-ignore Twilio Voice Call.status()
      const status = typeof connection.status === 'function' ? connection.status() : '';

      try {
        if (status === 'pending' || status === 'ringing') {
          TwilioVoiceService.markUserInitiatedDisconnect('Main.handleCallHangup.reject');
          // @ts-ignore
          connection.reject();
        } else if (status === 'connecting' || status === 'open') {
          TwilioVoiceService.markUserInitiatedDisconnect('Main.handleCallHangup.disconnect');
          // @ts-ignore
          connection.disconnect();
        }
      } catch (err) {
        logger.warn('Incoming decline: reject/disconnect threw', LogTitle, { err, status });
      }

      CallNotificationService.hideIncomingCallNotification(
        callDetails?.callRecordId
      );
    }

    declineUiTimerRef.current = window.setTimeout(() => {
      declineUiTimerRef.current = null;
      dispatch(updateCallBarState(CallBarState.IDLE));
      dispatch(setIncomingCallIdle());
    }, INCOMING_DECLINE_UI_DEFER_MS);
  }, [dispatch, clearPendingIncomingTimers]);

  const renderBodyContent = () => {
    let result = null;

    if (dialpadOpen || showNavigationDialler) {
      result = (
        <NavigationDiallerContainer
          id="Main-NavigationDiallerContainer"
          authData={authData}
          settingsData={settingsData}
          onCallHangup={handleCallHangupClicked}
          selectedSfContact={selectedSfContact}
          onNavigationDiallerUnmount={handleNavigationDiallerUnmount}
          onBackSpaceClicked={() => setSelectedSfContact(null)}
          dialOutOptions={dialOutOptions}
          getHelpAvailable={getHelpAvailable}
        />
      )
    } else if (showCallOutcome && !isCallDispositionNotMandatory) {
      const callRecord = authData?.lastCallRecord ? authData?.lastCallRecord : {callRecordId: authData?.userWrapUpStatus?.callRecordID};
      result = (
        <CallOutcome
          callRecord={callRecord}
          onCallOutcomeSubmit={handleCalloutcomeSubmitClick}
        />
      )
    } else {
      result = <HomeComponent onCallWrapup={handleCallWrapup}/>
    }

    return result;
  };

  return (
    <>
      {callIncoming && (
        <IncomingCallPanel id='IncomingCallPanel-Component' onCallAnswerPress={handleCallAnswer} onCallHangupPress={handleCallHangup}/>
      )}
      {!callIncoming && (
        <>
          {
            <TopNavBar
              id='TopNavBar'
              disableDialpad={callBarStateData !== CallBarState.IDLE}
              onDialPadClicked={handleDialPadClick}
              onHomeClicked={handleHomeClick}
              dialOutOptions={dialOutOptions}
            />
          }
          {renderBodyContent()}
        </>
      )}
    </>
  );
});

MainComponent.defaultProps = {
};

export default MainComponent;
